<template>
  <div v-if="!mobileMenusStore.mobileFilterStatus" class="mobile-menu fixed bottom-0 left-0 w-full z-50 ignore-close">
    <transition name="fade">
      <nav v-if="menuIsOpened" class="fixed top-0 right-0 bottom-0 left-0 w-full h-full bg-app-primary-1-red--fire-brick flex flex-col overflow-hidden">
        <div class="flex flex-row content-center">
          <div class="py-4 px-6">
            <NuxtLink to="/" :title="t_('logo-label')" :aria-label="t_('logo-label')" @click="navigationClick('Home')">
              <AtomBrandConcertgebouwLogo :aria-hidden="true" role="presentation" class="text-white w-24" />
            </NuxtLink>
          </div>
          <div class="ml-auto py-4 px-6 content-end justify-end items-end self-end leading-3 uppercase text-xs text-app-primary-1-orange--bittersweet">
            <AtomLink
              v-for="{ language, url, isCurrent } in pageStore.languageSwitches"
              :key="language"
              :url="url"
              class="mr-1"
              :class="{ 'cursor-pointer text-white': !isCurrent }"
            >
              {{ language }}
            </AtomLink>
          </div>
        </div>
        <div class="px-6 py-6 bg-app-primary-grey--black-squeeze mb-0 grow pb-12 -mb-12 rounded-tr-3xl text-xl h-screen pb-32 overflow-x-hidden overflow-y-scroll">
          <ol id="main-navigation" role="menu" :aria-label="t_('menu')">
            <li
              v-for="{ children, id, isActive, isOpen, title, url } in menu"
              :key="id"
              role="menuitem"
              class="border-b-2 border-solid border-white text-app-primary-1-red--fire-brick leading-none"
              @click="navigationStore.toggleOpen(id)"
            >
              <AtomLink
                :url="!children.length ? url : undefined"
                fallback-component="button"
                class="flex flex-row w-full items-center cursor-pointer font-medium pb-3 pt-4"
                :class="{ underline: isActive }"
                :aria-expanded="children.length ? (isOpen ? 'true' : 'false') : undefined"
                @click="!children.length ? navigationClick(title) : undefined"
              >
                <span>{{ title }}</span>
                <AtomIconChevronDown
                  v-if="children.length"
                  class="w-4 h-4 ml-auto ml-2 text-app-primary-1-red--torch transition duration-300 ease-in-out transform cursor-pointer"
                  :class="{ 'rotate-180': isOpen }"
                />
              </AtomLink>
              <transition name="fade">
                <div v-if="isOpen" class="block w-full pt-4 mt-3 border-t-2 border-solid border-white">
                  <ol class="pb-4" aria-label="Sub navigation">
                    <li v-for="child in children" :key="child.id" class="text-black text-base cursor-pointer mb-6 last:mb-0" role="menuitem">
                      <MoleculeCardMenuCategory :child="child" :small="true" @navigation-click="navigationClick" @toggle-menu="toggleMenuIsOpened" />
                    </li>
                  </ol>
                </div>
              </transition>
            </li>

            <li v-for="{ id, title, url } in serviceMenu" :key="id" role="menuitem" class="text-sm pb-3 pt-4 text-black leading-none font-medium first:mt-5">
              <AtomLink :url="url">
                {{ title }}
              </AtomLink>
            </li>
          </ol>
        </div>
        <transition-group name="fade">
          <div v-if="searching" key="background" class="w-full h-full bg-black right-0 bottom-0 left-0 opacity-75 rounded-tr-3xl fixed background-overlay"></div>
          <div v-if="searching" key="search_input" class="fixed mobile-search w-full max-w-95">
            <form name="mobile-search">
              <MoleculeInputWithIcon :placeholder="t_('search-placeholder-mobile')" :large="true" icon="icon-search" :autofocus="true" @submit="submitSearch" />
            </form>
          </div>
        </transition-group>
      </nav>
    </transition>

    <transition-group name="fade">
      <div v-if="searching" key="background" class="w-full h-full bg-black right-0 bottom-0 left-0 top-0 opacity-75 fixed"></div>
      <div v-if="searching" key="search_input" class="fixed mobile-search w-full max-w-95">
        <MoleculeInputWithIcon ref="searchInputMobile" :placeholder="$t('mobile-search-placeholder')" :large="true" icon="search" @submit="submitSearch" />
      </div>
    </transition-group>

    <div class="relative w-full sm:max-w-sm mx-auto pt-3 pb-1 rounded-tr-3xl rounded-tl-3xl bg-white z-10 v-shadow-mobile-menu">
      <ol class="flex flex-row justify-center text-app-primary-1-red--fire-brick">
        <li>
          <button class="flex flex-col items-center px-3 hover:text-app-primary-1-red--torch transition duration-300 ease-in-out cursor-pointer" @click="toggleSearching">
            <AtomIcon :icon="searching ? 'x' : 'app-search'" classes="w-5 h-5 mb-1" />
            <span class="text-xs"> {{ t_('search') }} </span>
          </button>
        </li>
        <li>
          <AtomLink
            :url="globalStore.eventOverview.url"
            class="flex flex-col items-center px-3 hover:text-app-primary-1-red--torch transition duration-300 ease-in-out cursor-pointer"
            @click="navigationClick(t_('concerts-and-tickets'))"
          >
            <AtomIconCalendar class="w-5 h-5 mb-1" />
            <span class="text-xs"> {{ t_('concerts-and-tickets') }} </span>
          </AtomLink>
        </li>
        <li>
          <button class="flex flex-col items-center px-3 hover:text-app-primary-1-red--torch transition duration-300 ease-in-out cursor-pointer" @click="toggleMenuIsOpened">
            <AtomIcon :icon="menuIsOpened ? 'x' : 'hamburger'" classes="w-5 h-5 mb-1" />
            <span class="text-xs"> {{ t_('menu') }} </span>
          </button>
        </li>
        <li v-if="tixOrder">
          <AtomLink :url="tixOrderUrl" class="flex flex-col items-center px-3 hover:text-app-primary-1-red--torch transition duration-300 ease-in-out cursor-pointer">
            <div class="relative">
              <AtomIconBasket class="w-5 h-5 mb-1" />
              <AtomBasketNumber class="absolute right-0 top-0 -mt-2 -mr-4">
                <span>{{ tixOrderSize }}</span>
              </AtomBasketNumber>
            </div>
            <span class="text-xs"> {{ t_('basket') }} </span>
          </AtomLink>
        </li>
        <li>
          <AtomLink
            v-if="favoritesEnabled"
            :url="globalStore.favoritePage.url"
            class="flex flex-col items-center px-3 hover:text-app-primary-1-red--torch transition duration-300 ease-in-out cursor-pointer"
          >
            <AtomIconBookmark class="w-5 h-5 mb-1" />
            <span class="v-service-menu-hover text-xs mr-1"> {{ t_('favorites') }}</span>
          </AtomLink>
          <button
            v-else
            class="flex flex-col items-center px-3 hover:text-app-primary-1-red--torch transition duration-300 ease-in-out cursor-pointer"
            @click="favoritesOpenOverlay()"
          >
            <AtomIconBookmark class="w-5 h-5 mb-1" />
            <span class="v-service-menu-hover text-xs mr-1"> {{ t_('favorites') }}</span>
          </button>
        </li>
        <li>
          <AtomLink
            :url="tixUser ? tixProfileUrl : tixLoginUrl"
            target="_self"
            rel="nofollow"
            class="flex flex-col items-center px-3 hover:text-app-primary-1-red--torch transition duration-300 ease-in-out cursor-pointer"
          >
            <span aria-hidden="true" class="relative inline-block">
              <AtomIconAppPerson class="w-5 h-5 inline-block" />
              <MoleculeABTestWrapper experiment-code="cv11" default-variant="A">
                <template #A>
                  <AtomIconCircledCheckFilled v-if="tixUser" class="text-app-secondary-8-blue--medium-aquamarine absolute w-3 h-3 inline-block bottom-[-3px] right-[-3px]" />
                </template>
                <template #B>
                  <AtomIconCircledCheckFilled v-if="tixUser" class="text-[#1E9250] absolute w-3 h-3 inline-block bottom-[-3px] right-[-3px]" />
                  <AtomIconCircledCrossFilled v-if="!tixUser" class="text-[#CE072F] absolute w-3 h-3 inline-block bottom-[-3px] right-[-3px]" />
                </template>
              </MoleculeABTestWrapper>
            </span>
            <span class="text-xs"> {{ t_(tixUser ? 'profile' : 'login') }} </span>
          </AtomLink>
        </li>
      </ol>
    </div>
  </div>
</template>

<script setup lang="ts">
import { explodeUrl } from '@/assets/link'

const router = useRouter()
const navigationStore = useNavigationStore()
const globalStore = useGlobalStore()
const favoritesStore = useFavoritesStore()
const pageStore = usePageStore()
const mobileMenusStore = useMobileMenusStore()
const { tixOrder, tixOrderUrl, tixOrderSize, tixProfileUrl, tixUser, tixLoginUrl } = setupTix()
const { t_ } = setupTranslationPrefixer('navigation', null)
const navigationClick = setupGtmNavigationClick()

const menuIsOpened = ref(false)
const searching = ref(false)
const searchInputMobile = ref<HTMLInputElement | null>(null)

const menu = computed(() => navigationStore.menu)
const serviceMenu = computed(() => navigationStore.service)
const favoritesEnabled = computed(() => favoritesStore.enabled)

const toggleMenuIsOpened = () => {
  searching.value = false
  menuIsOpened.value = !menuIsOpened.value

  if (menuIsOpened.value) {
    /*
     * BELOW IS COMMENTED FOR NOW, IT DOES NOT WORK PROPERLY YET AND BECAME A SHOWSTOPPER FOR THE SITE TO GO LIVE
     */
    // document.body.classList.add('overflow-hidden')
  } else {
    document.body.classList.remove('overflow-hidden')
  }
}

const toggleSearching = () => {
  searching.value = !searching.value

  if (searching.value) {
    menuIsOpened.value = false
    document.body.classList.add('overflow-hidden')

    setTimeout(() => {
      searchInputMobile.value?.focus()
    }, 100)
  } else {
    document.body.classList.remove('overflow-hidden')
  }
}

const submitSearch = (value: string) => {
  toggleSearching()
  searching.value = false
  menuIsOpened.value = false
  document.body.classList.remove('overflow-hidden')

  const path = explodeUrl(globalStore.search?.url || '').path
  router.push({ path, query: { keyword: value } })
}

const favoritesOpenOverlay = () => {
  favoritesStore.openOverlayAndStoreId()
}
</script>

<style lang="scss" scoped>
.mobile-menu {
  // On Windows 10, the default display scaling is set to 150%, which caused the navbar links to wrap onto two lines at the xl breakpoint. To fix this issue, the breakpoint was adjusted accordingly.
  @media (min-width: 1290px) {
    display: none;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.mobile-search {
  top: 15%;
  left: 50%;
  transform: translateX(-50%);
}

.max-w-95 {
  max-width: 95%;
}

.close-button {
  padding: 6px;
}

.v-shadow-mobile-menu {
  box-shadow:
    0 -2px 6px -1px rgba(0, 0, 0, 0.1),
    0 -2px 4px -1px rgba(0, 0, 0, 0.06);
}
</style>
